import { Button } from "@9amhealth/shared";
import type { FC } from "react";
import React from "react";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { authenticationState } from "src/state/state";

type UserCandidate = {
  userId: string | null;
  storage: string;
  priority:
    | "app_access"
    | "fullAuth"
    | "partialAuth"
    | "anonymous"
    | "noAuth"
    | "nullAuth"
    | "invalid";
  hasAppAccess: boolean;
  authStage: string | null;
  lastActiveTime: number;
};

type UserDebugInfo = {
  userId: string;
  refreshTokenSub: string | null;
  storage: string;
  lastActive: string;
  lastActiveTime: number;
  hasRefreshToken: boolean;
  refreshTokenValid: boolean;
  authStageInPrimaryStorage: string | null;
  authStageInOtherStorage: string | null;
  hasDataConflict: boolean;
  hasAppAccess: boolean;
  priority:
    | "app_access"
    | "fullAuth"
    | "partialAuth"
    | "anonymous"
    | "noAuth"
    | "nullAuth"
    | "invalid";
  isCurrentlyActive: boolean;
};

const DebugUserStorage: FC = () => {
  const [users, setUsers] = React.useState<UserDebugInfo[]>([]);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [userSelectionResult, setUserSelectionResult] = React.useState<{
    selectedUserId: string | null;
    selectedStorage: string | null;
    allCandidates: UserCandidate[];
  } | null>(null);

  const loadUsers = React.useCallback(() => {
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    const allStorageKeys = [...localStorageKeys, ...sessionStorageKeys];

    // Look for refresh tokens to find users (more reliable than lastActive)
    // Note: Storage keys have UUIDs without hyphens
    const refreshTokenRegex = /9am-([a-z0-9]+)-refreshToken/;
    const refreshTokenKeys = allStorageKeys.filter((k) =>
      k.match(refreshTokenRegex)
    );

    const userData: (UserDebugInfo | undefined)[] = [];
    const currentActiveUserId = StorageController.activeUserId;

    for (const refreshTokenKey of refreshTokenKeys) {
      const userId = refreshTokenKey.match(refreshTokenRegex)?.[1];
      if (!userId) continue;

      const storageType =
        localStorage.getItem(refreshTokenKey) !== null
          ? "localStorage"
          : "sessionStorage";
      const storage =
        storageType === "localStorage" ? localStorage : sessionStorage;
      const otherStorage =
        storageType === "localStorage" ? sessionStorage : localStorage;

      // Get last active time if available
      const lastActiveKey = `9am-${userId}-lastActive`;
      const lastActiveValue =
        storage.getItem(lastActiveKey) ?? Date.now().toString();
      const lastActiveTime = parseInt(lastActiveValue, 10);

      // Get the refresh token
      const refreshToken = storage.getItem(refreshTokenKey);
      let refreshTokenValid = false;
      let refreshTokenSub: string | null = null;

      if (refreshToken) {
        try {
          const parsed = JSON.parse(atob(refreshToken.split(".")[1])) as {
            sub?: string;
          };
          refreshTokenValid = Boolean(parsed.sub);
          refreshTokenSub = parsed.sub ?? null;
        } catch {
          refreshTokenValid = false;
        }
      }

      // Check auth stage in both storages to detect conflicts
      const authStageInPrimaryStorage = storage.getItem(
        `9am-${userId}-authStage`
      );
      const authStageInOtherStorage = otherStorage.getItem(
        `9am-${userId}-authStage`
      );
      const hasDataConflict =
        authStageInPrimaryStorage !== authStageInOtherStorage &&
        authStageInOtherStorage !== null;

      const hasAppAccess =
        storage.getItem(`9am-${userId}-hasAppAccess`) === "true";

      // Determine priority based on the new logic
      let priority: UserDebugInfo["priority"] = "invalid";
      if (hasAppAccess) {
        priority = "app_access";
      } else if (authStageInPrimaryStorage) {
        priority = authStageInPrimaryStorage as UserDebugInfo["priority"];
      }

      const isCurrentlyActive = refreshTokenSub === currentActiveUserId;

      userData.push({
        userId,
        refreshTokenSub,
        storage: storageType,
        lastActive: lastActiveValue,
        lastActiveTime,
        hasRefreshToken: !!refreshToken,
        refreshTokenValid,
        authStageInPrimaryStorage,
        authStageInOtherStorage,
        hasDataConflict,
        hasAppAccess,
        priority,
        isCurrentlyActive
      });
    }

    // Sort by priority, then by last active time
    userData.filter(Boolean).sort((a, b) => {
      // First by app access
      if (a.hasAppAccess !== b.hasAppAccess) {
        return a.hasAppAccess ? -1 : 1;
      }
      // Then by auth stage priority
      const authOrder = [
        "fullAuth",
        "partialAuth",
        "anonymous",
        "noAuth",
        "nullAuth"
      ];
      const aIndex = authOrder.indexOf(
        a.authStageInPrimaryStorage ?? "nullAuth"
      );
      const bIndex = authOrder.indexOf(
        b.authStageInPrimaryStorage ?? "nullAuth"
      );
      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }
      // Finally by last active time
      return b.lastActiveTime - a.lastActiveTime;
    });

    setUsers(userData.filter(Boolean));

    // Simulate user selection logic for debugging
    const bestUser =
      userData.filter(Boolean).find((u) => u.hasAppAccess) ?? userData[0];
    setUserSelectionResult({
      selectedUserId: bestUser?.refreshTokenSub ?? null,
      selectedStorage: bestUser?.storage ?? null,
      allCandidates: userData.filter(Boolean).map((u) => ({
        userId: u.refreshTokenSub,
        storage: u.storage,
        priority: u.priority,
        hasAppAccess: u.hasAppAccess,
        authStage: u.authStageInPrimaryStorage,
        lastActiveTime: u.lastActiveTime
      }))
    });
  }, [refreshKey]);

  React.useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const logDetails = () => {
    const oneBigJson: Record<string, unknown> = {
      currentUserId: StorageController.activeUserId,
      totalUsers: users.length,
      activeStorage:
        StorageController.useStorage === localStorage
          ? "localStorage"
          : "sessionStorage",
      currentAuthStage: authenticationState.state.authStage
    };
    const localStorageKeys = Object.keys(localStorage);
    const sessionStorageKeys = Object.keys(sessionStorage);
    const allStorageKeys = [...localStorageKeys, ...sessionStorageKeys];
    allStorageKeys.forEach((key) => {
      if (key.startsWith("9am-")) {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        const value = localStorage.getItem(key) || sessionStorage.getItem(key);
        if (value) {
          oneBigJson[key] = {
            value: value,
            storage: localStorage.getItem(key)
              ? "localStorage"
              : "sessionStorage",
            lastActiveTime: Date.now(),
            userId: key.split("-")[1] // Assuming userId is the second part of the key
          };
        }
      }
    });
    // eslint-disable-next-line no-console
    console.log("All storage data:", JSON.stringify(oneBigJson, null, 2));
  };

  const moveUser = (userId: string, fromStorage: string) => {
    const sourceStorage =
      fromStorage === "localStorage" ? localStorage : sessionStorage;
    const targetStorage =
      fromStorage === "localStorage" ? sessionStorage : localStorage;

    // Get all keys for this user
    const allKeys = Object.keys(sourceStorage).filter((key) =>
      key.includes(userId)
    );

    // Move each key to the other storage
    allKeys.forEach((key) => {
      const value = sourceStorage.getItem(key);
      if (value) {
        targetStorage.setItem(key, value);
        sourceStorage.removeItem(key);
      }
    });

    // Refresh the list
    setRefreshKey((prev) => prev + 1);
  };

  const deleteUser = (userId: string) => {
    const keys = [...Object.keys(localStorage), ...Object.keys(sessionStorage)];
    const userKeys = keys.filter((key) => key.includes(userId));

    userKeys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Refresh the list
    setRefreshKey((prev) => prev + 1);
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const forceLogActive = () => {
    // Force log the current active user
    StorageController.logActive();
    // Reload users after logging
    setTimeout(() => {
      loadUsers();
    }, 100);
  };

  return (
    <div>
      <Button size="medium" onPress={loadUsers}>
        Load Users
      </Button>
      <Button size="medium" onPress={logDetails}>
        Log Details
      </Button>
      <Button size="medium" onPress={forceLogActive}>
        Force Log Active
      </Button>

      <div style={{ marginTop: "10px" }}>
        <p>
          <strong>Current State:</strong>
        </p>
        <p>• Found {users.length} users</p>
        <p>
          • Active user: <code>{StorageController.activeUserId ?? "none"}</code>
        </p>
        <p>
          • Current storage:{" "}
          <code>
            {StorageController.useStorage === localStorage
              ? "localStorage"
              : "sessionStorage"}
          </code>
        </p>
        <p>
          • Current auth stage:{" "}
          <code>{authenticationState.state.authStage}</code>
        </p>

        {userSelectionResult && (
          <div
            style={{
              marginTop: "10px",
              padding: "8px",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px"
            }}
          >
            <p>
              <strong>User Selection Logic:</strong>
            </p>
            <p>
              • Would select:{" "}
              <code>{userSelectionResult.selectedUserId || "none"}</code>
            </p>
            <p>
              • From storage:{" "}
              <code>{userSelectionResult.selectedStorage || "none"}</code>
            </p>
            <p>
              • Total candidates: {userSelectionResult.allCandidates.length}
            </p>
          </div>
        )}

        {users.some((u) => u.hasDataConflict) && (
          <div
            style={{
              marginTop: "10px",
              padding: "8px",
              backgroundColor: "#ffebee",
              borderRadius: "4px",
              color: "#c62828"
            }}
          >
            <p>
              <strong>⚠️ Data Conflicts Detected!</strong>
            </p>
            <p>
              Some users have different authStage values in localStorage vs
              sessionStorage.
            </p>
          </div>
        )}
      </div>

      {users.length > 0 && (
        <div style={{ marginTop: "10px", fontSize: "10px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "10px"
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  User ID
                </th>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  Storage
                </th>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  Last Active
                </th>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  Token
                </th>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  Auth Stage (Primary)
                </th>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  Auth Stage (Other)
                </th>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  App Access
                </th>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  Priority
                </th>
                <th
                  style={{
                    border: "1px solid #ccc",
                    padding: "2px",
                    fontSize: "9px"
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => {
                const rowStyle = {
                  backgroundColor: user.isCurrentlyActive
                    ? "#e8f5e8"
                    : user.hasDataConflict
                      ? "#ffebee"
                      : "transparent"
                };
                return (
                  <tr key={index} style={rowStyle}>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        fontSize: "9px"
                      }}
                    >
                      <div
                        style={{
                          fontWeight: user.isCurrentlyActive ? "bold" : "normal"
                        }}
                      >
                        {user.refreshTokenSub ?? user.userId}
                      </div>
                      {user.isCurrentlyActive && (
                        <div style={{ fontSize: "8px", color: "green" }}>
                          ★ ACTIVE
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        fontSize: "9px"
                      }}
                    >
                      {user.storage}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        fontSize: "8px"
                      }}
                    >
                      {formatTime(user.lastActiveTime)}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        fontSize: "9px"
                      }}
                    >
                      {user.refreshTokenValid ? "✅" : "❌"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        fontSize: "9px"
                      }}
                    >
                      <div
                        style={{
                          fontWeight: user.hasDataConflict ? "bold" : "normal"
                        }}
                      >
                        {user.authStageInPrimaryStorage ?? "none"}
                      </div>
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        fontSize: "9px"
                      }}
                    >
                      <div
                        style={{
                          color: user.hasDataConflict ? "#c62828" : "#666",
                          fontWeight: user.hasDataConflict ? "bold" : "normal"
                        }}
                      >
                        {user.authStageInOtherStorage ?? "none"}
                      </div>
                      {user.hasDataConflict && (
                        <div style={{ fontSize: "8px", color: "red" }}>⚠️</div>
                      )}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        fontSize: "9px"
                      }}
                    >
                      {user.hasAppAccess ? "✅" : "❌"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "2px",
                        fontSize: "8px"
                      }}
                    >
                      <div
                        style={{
                          color:
                            user.priority === "app_access" ? "green" : "#666",
                          fontWeight:
                            user.priority === "app_access" ? "bold" : "normal"
                        }}
                      >
                        {user.priority}
                      </div>
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "2px" }}>
                      <button
                        onClick={() => moveUser(user.userId, user.storage)}
                        style={{
                          fontSize: "7px",
                          margin: "1px",
                          padding: "2px"
                        }}
                      >
                        Move to{" "}
                        {user.storage === "localStorage" ? "Session" : "Local"}
                      </button>
                      <br />
                      <button
                        onClick={() => deleteUser(user.userId)}
                        style={{
                          fontSize: "7px",
                          margin: "1px",
                          padding: "2px",
                          color: "red"
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DebugUserStorage;
